import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  SecondaryButton,
  Icons,
  Modal,
  PrimaryButton,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  sendOffer,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import { BooleanEnum } from '../../util/enums.js';
import { showErrorToast, showSuccessToast } from '../../util/toast.js';
import { createResourceLocatorString } from '../../util/routes.js';
import OfferModal from './OfferModal/OfferModal.js';
import SectionMostPopular from './SectionMostPopular.js';
import SectionYouMayLike from './SectionYouMayLike.js';
import AuthorListingsSection from './AuthorListingsSection.js';
import { withViewport } from '../../util/uiHelpers.js';
import BreadCrumbs from './BreadCrumbs.js';
import SpecificationsSection from './SpecificationsSection.js';
import { MOBILE_BREAKPOINT, MODAL_BREAKPOINT } from '../../util/constants.js';
import RenderMobileInformationMaybe from './RenderMobileInformationMaybe/RenderMobileInformationMaybe.js';
import { SharetribeApi } from '../../util/api.js';
import PayoutDetailsNotice from './PayoutDetailsNotice/PayoutDetailsNotice.js';
import ProductSizeHighlight from './ProductSizeHighlight.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(BooleanEnum.FALSE);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    similarListings,
    viewport,
    ...restOfProps
  } = props;
  const dispatch = useDispatch();
  const [openIndex, setOpenIndex] = useState(false);
  const [open, setOpen] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);
  const handleModalOpen = () => {
    setOpenShareModal(true);
  };
  const onOpenShareModal = () => {
    setOpenShareModal(!openShareModal);
  };

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;
  const actionBar =
    mounted && currentListing.id && isOwnListing ? (
      <>
        {noPayoutDetailsSetWithOwnListing ? (
          <ActionBarMaybe
            className={css.actionBarForProductLayout}
            isOwnListing={isOwnListing}
            listing={currentListing}
            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
          />
        ) : null}
        <ActionBarMaybe
          className={css.actionBarForProductLayout}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      </>
    ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const handleOfferSubmit = async values => {
    try {
      const result = await dispatch(
        sendOffer({
          price: values?.price,
          listing: currentListing,
        })
      );
      if (result?.id?.uuid) {
        const transactionId = result.id.uuid;
        const response = await SharetribeApi.createTxNotificationApi({ txId: transactionId });
        history.push(
          createResourceLocatorString(
            'DetailedInboxPage',
            routeConfiguration,
            { txId: transactionId },
            {}
          )
        );
      } else {
        showErrorToast(intl.formatMessage({ id: 'ListingPage.offer.submit.failure' }));
      }
    } catch (error) {
      showErrorToast(intl.formatMessage({ id: 'ListingPage.offer.submit.error' }));
    } finally {
      setIsOfferModalOpen(BooleanEnum.FALSE);
    }
  };

  const isModalBreakpoint =
    viewport?.width && viewport?.width > 0 && viewport?.width < MODAL_BREAKPOINT;
  const isMobileLayout =
    viewport?.width && viewport?.width > 0 && viewport?.width < MOBILE_BREAKPOINT;
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={css.listingPageWrapper}>
          <div className={css.fixedWidthContainer}>
            <BreadCrumbs listingTitle={title} />
            {actionBar}
            <div className={css.contentWrapperForProductLayout}>
              <div className={css.mainContentWrapper}>
                <div className={css.mainColumnForProductLayout}>
                  {showListingImage && (
                    <SectionGallery
                      viewport={viewport}
                      listing={currentListing}
                      variantPrefix={config.layout.listingImage.variantPrefix}
                    />
                  )}
                </div>

                {isMobileLayout && (
                  <RenderMobileInformationMaybe
                    title={
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    }
                    titleDesktop={
                      <H4 as="h1" className={css.orderPanelTitle}>
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      </H4>
                    }
                    listing={currentListing}
                    intl={intl}
                    config={config}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    author={ensuredAuthor}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    authorBlock={
                      <SectionAuthorMaybe
                        title={title}
                        listing={currentListing}
                        authorDisplayName={authorDisplayName}
                        onContactUser={onContactUser}
                        isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                        onCloseInquiryModal={() => setInquiryModalOpen(false)}
                        sendInquiryError={sendInquiryError}
                        sendInquiryInProgress={sendInquiryInProgress}
                        onSubmitInquiry={onSubmitInquiry}
                        currentUser={currentUser}
                        onManageDisableScrolling={onManageDisableScrolling}
                      />
                    }
                  />
                )}
                <div className={css.orderColumnForProductLayout}>
                  {!isMobileLayout ? <ProductSizeHighlight publicData={publicData} /> : null}
                  <OrderPanel
                    className={css.productOrderPanel}
                    listing={currentListing}
                    isOwnListing={isOwnListing}
                    onSubmit={handleOrderSubmit}
                    authorLink={
                      <NamedLink
                        className={css.authorNameLink}
                        name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                        params={params}
                        to={{ hash: '#author' }}
                      >
                        {authorDisplayName}
                      </NamedLink>
                    }
                    title={
                      <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                    }
                    titleDesktop={
                      <H4 as="h1" className={css.orderPanelTitle}>
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      </H4>
                    }
                    payoutDetailsWarning={payoutDetailsWarning}
                    author={ensuredAuthor}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onContactUser={onContactUser}
                    {...restOfProps}
                    validListingTypes={config.listing.listingTypes}
                    marketplaceCurrency={config.currency}
                    dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
                    marketplaceName={config.marketplaceName}
                    showListingImage={showListingImage}
                    setIsOfferModalOpen={setIsOfferModalOpen}
                    viewport={viewport}
                    authorBlock={
                      <SectionAuthorMaybe
                        title={title}
                        listing={currentListing}
                        authorDisplayName={authorDisplayName}
                        onContactUser={onContactUser}
                        isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                        onCloseInquiryModal={() => setInquiryModalOpen(false)}
                        sendInquiryError={sendInquiryError}
                        sendInquiryInProgress={sendInquiryInProgress}
                        onSubmitInquiry={onSubmitInquiry}
                        currentUser={currentUser}
                        onManageDisableScrolling={onManageDisableScrolling}
                      />
                    }
                    isModalBreakpoint={isModalBreakpoint}
                  />{' '}
                  {/* <SectionYouMayLike listings={similarListings} viewport={viewport} /> */}
                  {/* Specifications */}
                  <SpecificationsSection publicData={publicData} />
                  <div className={css.shoppingWithUs}>
                    <h2 onClick={() => setOpenIndex(!openIndex)}>
                      <FormattedMessage
                        id="ListingPage.shopping.title"
                        values={{ marketplaceName }}
                      />{' '}
                      <Icons name={openIndex ? 'minus' : 'plus'} />
                    </h2>

                    {openIndex ? (
                      <>
                        <div className={css.shoppingBlock}>
                          <Icons name="payLater" />
                          <div className={css.shoppingInfo}>
                            <h2>
                              <FormattedMessage id="ListingPage.shopping.block1.title" />
                            </h2>
                            <p>
                              <FormattedMessage
                                id="ListingPage.shopping.block1.desc"
                                values={{ marketplaceName }}
                              />
                            </p>
                          </div>
                        </div>

                        <div className={css.shoppingBlock}>
                          <Icons name="protection" />
                          <div className={css.shoppingInfo}>
                            <h2>
                              <FormattedMessage
                                id="ListingPage.shopping.block2.title"
                                values={{ marketplaceName }}
                              />
                            </h2>
                            <p>
                              <FormattedMessage id="ListingPage.shopping.block2.desc" />
                            </p>
                          </div>
                        </div>

                        <div className={css.shoppingBlock}>
                          <Icons name="delivery" />
                          <div className={css.shoppingInfo}>
                            <h2>
                              <FormattedMessage id="ListingPage.shopping.block3.title" />
                            </h2>
                            <p>
                              <FormattedMessage id="ListingPage.shopping.block3.desc" />
                            </p>
                          </div>
                        </div>

                        <div className={css.shoppingBlock}>
                          <Icons name="favorite" />
                          <div className={css.shoppingInfo}>
                            <h2>
                              <FormattedMessage id="ListingPage.shopping.block4.title" />
                            </h2>
                            <p>
                              <FormattedMessage id="ListingPage.shopping.block4.desc" />
                            </p>
                          </div>
                        </div>

                        <div className={css.shoppingBlock}>
                          <Icons name="star" />
                          <div className={css.shoppingInfo}>
                            <h2>
                              <FormattedMessage id="ListingPage.shopping.block5.title" />
                            </h2>
                            <p>
                              <FormattedMessage id="ListingPage.shopping.block5.desc" />
                            </p>
                          </div>
                        </div>
                      </>
                    ) : null}
                  </div>
                  {/* <div className={css.shoppingActions}>
                    <SecondaryButton>
                      <Icons name="soldTag" /> Have one to sell?
                    </SecondaryButton>{' '}
                    <SecondaryButton onClick={handleModalOpen}>
                      <Icons name="shoppingBag" /> Request bundle
                    </SecondaryButton>
                  </div> */}
                </div>
              </div>
            </div>

            <AuthorListingsSection
              listings={similarListings}
              viewport={viewport}
              listing={currentListing}
            />
          </div>

          <OfferModal
            isOpen={isOfferModalOpen}
            onClose={() => setIsOfferModalOpen(BooleanEnum.FALSE)}
            title={title}
            authorDisplayName={authorDisplayName}
            listing={currentListing}
            onSubmit={handleOfferSubmit}
            modalTitle={intl.formatMessage({ id: 'ListingPage.offer.modalTitle' })}
          />

          <Modal
            isOpen={openShareModal}
            onClose={() => {
              setOpenShareModal(false);
            }}
            onManageDisableScrolling={onManageDisableScrolling}
            modalTitle={intl.formatMessage({ id: 'ListingPage.bundle.modalTitle' })}
          >
            <div className={css.howToBundleContent}>
              <Icons name="shoppingBag" />
              <p>
                <FormattedMessage id="ListingPage.bundle.intro" />
              </p>
              <ol>
                <li>
                  <FormattedMessage id="ListingPage.bundle.step1" />
                </li>
                <li>
                  <FormattedMessage id="ListingPage.bundle.step2" />
                </li>
                <li>
                  <FormattedMessage id="ListingPage.bundle.step3" />
                </li>
                <li>
                  <FormattedMessage id="ListingPage.bundle.step4" />
                </li>
              </ol>
              <p>
                <FormattedMessage
                  id="ListingPage.bundle.guarantee"
                  values={{
                    buyerGuaranteeLink: (
                      <a href="/">
                        {intl.formatMessage({ id: 'ListingPage.bundle.guarantee.linkLabel' })}
                      </a>
                    ),
                  }}
                />
              </p>
              <PrimaryButton
                onClick={() => {
                  setOpenShareModal(false);
                }}
              >
                <FormattedMessage id="ListingPage.bundle.ok" />
              </PrimaryButton>
            </div>
          </Modal>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    similarListings: stateSimilarListingsId = [],
  } = state.ListingPage || {};

  const similarListings = getListingsById(state, stateSimilarListingsId);
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    similarListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport
)(EnhancedListingPage);

export default ListingPage;
